import { useEffect } from 'react';
import { referralsApi, marketingApi } from '../services/socialApi';
import { storeReferralCode, getReferralCode } from '../utils/referral';

// Re-exported so existing importers (e.g. src/pages/Download.js) keep their
// `import { getReferralCode } from '../hooks/useReferral'` working. The
// canonical implementation now lives in ../utils/referral (a leaf module,
// so services/socialApi.js can inject the code into signup payloads without
// an import cycle).
export { getReferralCode };

/**
 * useReferral — Captures referral code from URL query params on mount.
 * Stores in localStorage for attribution on registration, and notifies backend.
 *
 * Usage: Call once in SocialContext or App-level component.
 *   useReferral();
 */
export function useReferral() {
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('ref');
      if (!ref) return;

      // Channel attribution: count this CLICK against the channel code so the
      // flywheel's ref-tagged links (?ref=<channel>) become measurable — the
      // top-of-funnel signal for "which channel grows users." Anonymous-safe,
      // fire-and-forget; fires for ANY landing (download/pricing/join) because
      // this hook runs app-wide (SocialContext).
      marketingApi.track(ref, 'click', 'web').catch(() => {});

      // Store for attribution on registration. Writes BOTH the primary and
      // legacy keys so the signup payload (which now injects getReferralCode)
      // and the /join path agree — see ../utils/referral.
      storeReferralCode(ref);

      // Notify backend (fire-and-forget, don't block)
      const token = localStorage.getItem('access_token');
      if (token) {
        referralsApi.use({ code: ref }).catch(() => {});
      }

      // Clean URL without reload
      params.delete('ref');
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    } catch {
      // Non-critical — don't break the app
    }
  }, []);
}
